import { useMemo } from "react";

import type { AppointmentState } from "../types";
import { formatPhoneNumber } from "../utils";
import { compileTemplate } from "../utils/formatting";
import { getAppointmentTheme } from "../utils/theme";

interface AppointmentBookingFailedProps {
  appointment: AppointmentState;
}

const AppointmentBookingFailed = ({ appointment }: AppointmentBookingFailedProps) => {
  const { accentColor, gradientBackground } = useMemo(
    () => getAppointmentTheme(appointment),
    [appointment]
  );

  const shopName = appointment?.shopData?.shopName?.name || "the shop";
  const rawPhone = appointment?.shopData?.shopContact?.phone?.number || "";
  const formattedPhone = formatPhoneNumber(rawPhone) || rawPhone;
  const address = appointment?.shopData?.shopContact?.address;

  const title = appointment?.shopData?.messages?.unconfirmedTitle || "Appointment Request Received";

  const message = useMemo(() => {
    const raw =
      appointment?.shopData?.messages?.unconfirmedMessage ||
      "Your appointment has been requested, but not confirmed. {{shop_name}} has been notified and will reach out to confirm the details as soon as possible. If preferred, you can also call them to confirm your appointment.";
    return compileTemplate(raw, { shop_name: shopName });
  }, [appointment?.shopData?.messages?.unconfirmedMessage, shopName]);

  const requestedDate = appointment?.slot?.date || appointment?.date;
  const requestedTime = appointment?.slot?.time || appointment?.time;
  const requestedServices = appointment?.services
    ?.map((service) => service?.name || service?.value)
    .filter(Boolean)
    .join(", ");
  const requestedServiceLabel = requestedServices || appointment?.service;

  return (
    <div
      className="flex flex-col items-center min-h-full px-4 py-8 font-roboto"
      style={{ background: gradientBackground }}
    >
      <div className="w-full max-w-md">
        <div
          className="rounded-2xl bg-white px-6 py-6"
          style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        >
          <div className="mb-4 flex justify-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full"
              style={{
                backgroundColor: "#fef3c7",
              }}
            >
              <i
                className="fa-solid fa-triangle-exclamation text-3xl"
                style={{ color: "#d97706", lineHeight: 1 }}
                aria-hidden="true"
              />
            </div>
          </div>

          <div className="mb-2 text-center text-2xl font-bold text-gray-900">
            {title}
          </div>
          <div className="mb-5 text-center text-sm leading-relaxed text-gray-600">
            {message}
          </div>

          {(requestedDate || requestedTime || requestedServiceLabel) && (
            <div className="mb-5 rounded-xl border border-solid border-gray-200 bg-gray-50 px-4 py-3 text-center">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Requested Appointment
              </div>
              <div className="mt-1 text-sm font-semibold text-gray-900">
                {requestedDate || "Date TBD"}
                {requestedDate && requestedTime ? " at " : ""}
                {requestedTime || ""}
              </div>
              {requestedServiceLabel && (
                <div className="mt-1 text-sm text-gray-700">
                  {requestedServiceLabel}
                </div>
              )}
            </div>
          )}

          <div
            className="mb-5 rounded-xl border-2 border-solid px-4 py-4 text-center text-sm text-gray-700"
            style={{ borderColor: `${accentColor}44` }}
          >
            <div
              className="text-xs font-semibold uppercase tracking-wide mb-2"
              style={{ color: accentColor }}
            >
              Contact The Shop
            </div>
            <div className="font-semibold text-gray-900">{shopName}</div>
            {rawPhone && (
              <div className="mt-1">
                <a
                  href={`tel:${rawPhone.replace(/\D/g, "")}`}
                  target="_top"
                  className="hover:underline"
                  style={{ color: accentColor }}
                  onClick={(e) => {
                    if (window.parent !== window) {
                      e.preventDefault();
                      window.parent.postMessage(
                        { type: "DC_TEL_LINK", tel: `tel:${rawPhone.replace(/\D/g, "")}` },
                        "*"
                      );
                    }
                  }}
                >
                  {formattedPhone}
                </a>
              </div>
            )}
            {address && (
              <div className="mt-2">
                <div>{address.street}</div>
                <div>
                  {address.city}, {address.state} {address.zip}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export { AppointmentBookingFailed };
